import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faTimes, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { ConsultationService, sharedApiClient } from '../fetcher';
import { StatusBadge } from '@medprompt/shared';
import { ConsultationStatusEnum } from '../enums/consultationStatusEnum';
import AudioPlayer from '../scribe/AudioPlayer';
import useConsultationAudio from '../scribe/useConsultationAudio';
import '../detail/DocumentDetailView.css';

const ConsultationPreviewModal = ({ isOpen, onClose, consultationId, fromDocumentId, fromPatientNhsId, fromPatientId, fromPatientName }) => {
  const navigate = useNavigate();
  const [consultationData, setConsultationData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const consultationService = useMemo(() => new ConsultationService(sharedApiClient), []);

  useEffect(() => {
    if (!isOpen || !consultationId) {
      return;
    }

    let isCancelled = false;
    setLoading(true);
    setError(null);

    const fetchConsultation = async () => {
      try {
        const data = await consultationService.fetchConsultationData(consultationId);
        if (isCancelled) return;
        setConsultationData(data);
      } catch (err) {
        if (isCancelled) return;
        console.error('Error fetching consultation data:', err);
        setError('Failed to load consultation');
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchConsultation();
    return () => {
      isCancelled = true;
    };
  }, [isOpen, consultationId, consultationService]);

  const showAudioPlayer = Boolean(
    isOpen && !loading && !error && consultationData
    && consultationData.status !== 'RECORDING'
    && consultationData.status !== 'PENDING'
    && consultationData.status !== 'ABANDONED'
  );
  const audio = useConsultationAudio({ consultationId, consultationService, enabled: showAudioPlayer });

  const handleFullView = () => {
    onClose();
    const navState = {};
    
    if (fromDocumentId) {
      navState.fromDocumentId = fromDocumentId;
    }
    
    if (fromPatientNhsId) {
      navState.fromPatient = true;
      navState.patientNhsId = fromPatientNhsId;
      navState.patientId = fromPatientId;
      navState.patientName = fromPatientName;
    }
    
    navigate(`/consultation-detail/${consultationId}`, {
      state: Object.keys(navState).length > 0 ? navState : undefined
    });
  };

  if (!isOpen) return null;

  const statusEnum = consultationData?.status 
    ? ConsultationStatusEnum[consultationData.status] || ConsultationStatusEnum.PENDING 
    : ConsultationStatusEnum.PENDING;

  return (
    <div className="modal-overlay" style={{ zIndex: 1001 }}>
      <div className="modal-container" style={{ 
        maxWidth: '90vw', 
        maxHeight: '90vh', 
        width: '900px', 
        height: 'auto' 
      }}>
        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2>Consultation Preview</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button 
              className="patient-diary-toggle"
              onClick={handleFullView}
              title="Open in full view"
            >
              <FontAwesomeIcon icon={faExternalLinkAlt} />
              Full View
            </button>
            <button className="close-button" onClick={onClose}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>
        
        <div className="modal-content" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
              <FontAwesomeIcon icon={faSpinner} spin size="2x" />
              <span style={{ marginLeft: '10px' }}>Loading consultation...</span>
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <p style={{ color: '#e74c3c' }}>{error}</p>
            </div>
          ) : consultationData ? (
            <>
              <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                  <StatusBadge
                    label={statusEnum.description}
                    colorFamily={statusEnum.colorFamily || 'gray'}
                    size="normal"
                  />
                </div>
                
                {showAudioPlayer && (
                  <div style={{ marginTop: '10px' }}>
                    <AudioPlayer
                      audioUrl={audio.audioUrl}
                      loading={audio.audioLoading}
                      error={audio.audioError}
                    />
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ marginBottom: '10px', fontSize: '16px', fontWeight: '600' }}>Notes</h3>
                {consultationData.notes ? (
                  <div style={{ 
                    padding: '15px', 
                    backgroundColor: '#fff', 
                    border: '1px solid #ddd', 
                    borderRadius: '4px',
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'monospace',
                    fontSize: '13px',
                    lineHeight: '1.6',
                    maxHeight: '400px',
                    overflowY: 'auto'
                  }}>
                    {consultationData.notes}
                  </div>
                ) : (
                  <div style={{ 
                    padding: '15px', 
                    backgroundColor: '#f8f9fa', 
                    border: '1px solid #ddd', 
                    borderRadius: '4px',
                    fontStyle: 'italic',
                    color: '#666'
                  }}>
                    No notes available
                  </div>
                )}
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <p>Failed to load consultation data</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConsultationPreviewModal;
